/**
 * №22.3.1 (D-AF3 / P1) — the panel's HISTORY layer: the scope's rollup, resumable.
 *
 * A LIST, not a chat — there is deliberately NO composer here (a composer made the
 * rows read as chat lines; David's veto P1). It shows the conversations under the
 * CURRENT anchor (the D-CV9 rollup the stream already computes), newest / live first,
 * each row RESUMABLE — selecting one opens that task's thread in the panel (the
 * composer returns with it). "＋ New chat at @{anchor}" is a BUTTON row at the top.
 * The standard sidebar-history pattern; the drawer-v1 grammar is the visual reference.
 *
 * №22.3.2 (F2b) — this is now the ONLY home of the rollup. The panel's default body
 * used to render the same events as a stream directly above the composer, which read
 * as a chat that had spoken first (David's veto, twice). Runs, open decisions and
 * activity are here and nowhere else; the chat window opens empty with one line
 * pointing at this layer. Two consequences for the rows themselves:
 *
 *  - a row is a LABEL, not a message: its summary goes through `toRowLabel`, so the
 *    literal `**Billing Clerk**` an agent reply carries is stripped rather than
 *    rendered or leaked, and one long reply can never stretch a row into a paragraph;
 *  - the list is a list in the accessibility tree too (`role="list"`/`"listitem"`),
 *    which is what the F2 gate asserts.
 */
import { formatRelativeTime } from "../../lib/v3-columns";
import { toRowLabel } from "../chat/plain-text";
import type { PanelTaskFocus } from "./co-panel-context";
import type { StreamEvent } from "../../../lib/types";

interface Conversation {
  taskId: string;
  actorName: string;
  anchorLabel: string | null;
  summary: string;
  ts: string;
  live: boolean;
}

/** Roll the flat stream up into one row per task (its latest event), live/newest first. */
function toConversations(events: StreamEvent[]): Conversation[] {
  const byTask = new Map<string, Conversation>();
  for (const e of events) {
    if (!e.taskId) continue;
    const live = e.kind === "card" || e.status === "running";
    // A row LABEL, never a message: markup stripped, length capped (F2b).
    // №22.5 (C1) — a batched card says how many are waiting behind it. "3 unanswered
    // questions" and "1 unanswered question" are different amounts of work, and the
    // history row is where a supervisor decides which conversation to open first.
    const batchTail = e.kind === "card" && e.batch && e.batch.total > 1
      ? ` · ${e.batch.index + 1} of ${e.batch.total}`
      : "";
    const summary = toRowLabel(
      e.kind === "card" ? (e.reason ?? e.title)
        : e.kind === "run" ? (e.summary ?? e.title)
          : (e.summary ?? e.title),
    ) + batchTail;
    const existing = byTask.get(e.taskId);
    // Keep the newest event per task, but let a live event win the row's "live" flag.
    if (!existing) {
      byTask.set(e.taskId, {
        taskId: e.taskId,
        actorName: e.actor.name ?? e.agentName ?? "Kuvio",
        anchorLabel: e.anchorLabel ?? null,
        summary,
        ts: e.ts,
        live,
      });
    } else if (live && !existing.live) {
      existing.live = true;
      existing.summary = summary;
    }
  }
  return [...byTask.values()].sort((a, b) => (a.live === b.live ? (a.ts > b.ts ? -1 : 1) : a.live ? -1 : 1));
}

export function PanelHistory({
  events,
  loading,
  chip,
  onResume,
  onNewChat,
}: {
  events: StreamEvent[];
  loading: boolean;
  chip: string;
  onResume: (f: PanelTaskFocus) => void;
  onNewChat: () => void;
}) {
  const conversations = toConversations(events);

  return (
    <div
      className="copanel__hist"
      data-testid="copanel-history-list"
      data-layer="list"
      role="list"
      aria-label={`Work at ${chip}`}
    >
      <button type="button" className="copanel__histnew" onClick={onNewChat} data-testid="history-new">
        ＋ New chat at @{chip}
      </button>
      {loading && conversations.length === 0 && (
        <div className="copanel__loading"><div className="skeleton sk-line" style={{ width: "80%" }} /><div className="skeleton sk-line" style={{ width: "60%" }} /></div>
      )}
      {!loading && conversations.length === 0 && (
        <div className="copanel__empty">Nothing here yet. When work runs under this anchor it lands here — runs, open decisions, and what happened.</div>
      )}
      {conversations.map((c) => (
        <button
          type="button"
          key={c.taskId}
          role="listitem"
          className={`copanel__histrow ${c.live ? "is-live" : ""}`}
          onClick={() => onResume({ taskId: c.taskId, agentName: c.actorName, label: c.anchorLabel ?? undefined })}
          data-testid="history-row"
        >
          <div className="copanel__histl1">
            <span className="copanel__avm is-hire" aria-hidden="true">{c.actorName.slice(0, 2).toUpperCase()}</span>
            <b>{c.actorName}</b>
            {c.anchorLabel ? <span className="copanel__histanchor"> · {c.anchorLabel}</span> : null}
            <span className="copanel__histage">{c.live ? "● now" : formatRelativeTime(c.ts)}</span>
          </div>
          <div className="copanel__histsub">
            {c.summary}
            {!c.live ? <span className="copanel__histresume"> · resume →</span> : null}
          </div>
        </button>
      ))}
    </div>
  );
}
